import { useId, CSSProperties } from 'react'

interface FilterApparatusProps {
  vacuumOn?: boolean
  filterProgress?: number // 0-100
  filtrateFillLevel?: number // 0-100
  filtrateColor?: string
  precipitateAmount?: number // 0-100
  precipitateColor?: string
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const FilterApparatus = ({
  vacuumOn = false,
  filterProgress = 0,
  filtrateFillLevel = 0,
  filtrateColor = '#F5F5DC',
  precipitateAmount = 0,
  precipitateColor = '#FFFFFF',
  onClick,
  disabled = false,
  className = ''
}: FilterApparatusProps) => {
  const id = useId()

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  // Calculate filtrate level in flask
  const flaskFillHeight = (filtrateFillLevel / 100) * 80
  const flaskFillY = 200 - flaskFillHeight

  return (
    <svg
      viewBox="0 0 160 280"
      width={140}
      height={250}
      style={containerStyle}
      className={className}
      onClick={!disabled && onClick ? onClick : undefined}
    >
      <defs>
        {/* Glass gradient */}
        <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.7)" />
          <stop offset="15%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="50%" stopColor="rgba(220,235,250,0.25)" />
          <stop offset="85%" stopColor="rgba(255,255,255,0.35)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.6)" />
        </linearGradient>

        {/* Flask body gradient */}
        <linearGradient id={`flask-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
          <stop offset="20%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="50%" stopColor="rgba(220,235,250,0.2)" />
          <stop offset="80%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
        </linearGradient>

        {/* Filtrate gradient */}
        <linearGradient id={`filtrate-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={filtrateColor} stopOpacity="0.7" />
          <stop offset="100%" stopColor={filtrateColor} stopOpacity="0.9" />
        </linearGradient>

        {/* Precipitate gradient */}
        <linearGradient id={`precipitate-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={precipitateColor} stopOpacity="0.9" />
          <stop offset="100%" stopColor={precipitateColor} stopOpacity="1" />
        </linearGradient>

        {/* Rubber stopper gradient */}
        <linearGradient id={`rubber-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2C2C2C" />
          <stop offset="50%" stopColor="#4A4A4A" />
          <stop offset="100%" stopColor="#2C2C2C" />
        </linearGradient>

        {/* Shadow filter */}
        <filter id={`shadow-${id}`} x="-10%" y="-5%" width="120%" height="115%">
          <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.25" />
        </filter>

        {/* Clip path for flask body */}
        <clipPath id={`flask-clip-${id}`}>
          <path d="M45 120 L45 200 Q45 220 80 220 Q115 220 115 200 L115 120 Z" />
        </clipPath>

        {/* Filter paper clip path */}
        <clipPath id={`filter-clip-${id}`}>
          <ellipse cx="80" cy="52" rx="28" ry="8" />
        </clipPath>
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Vacuum Flask (Erlenmeyer style with side arm) */}
        <g>
          {/* Flask body */}
          <path
            d="M45 120 L45 200 Q45 220 80 220 Q115 220 115 200 L115 120 Z"
            fill={`url(#flask-${id})`}
            stroke="#7A9BB8"
            strokeWidth="2"
          />

          {/* Filtrate in flask */}
          {filtrateFillLevel > 0 && (
            <g clipPath={`url(#flask-clip-${id})`}>
              <rect
                x="46"
                y={flaskFillY}
                width="68"
                height={flaskFillHeight}
                fill={`url(#filtrate-${id})`}
              />
              {/* Liquid surface */}
              <ellipse
                cx="80"
                cy={flaskFillY}
                rx="34"
                ry="5"
                fill={filtrateColor}
                opacity="0.6"
              />
            </g>
          )}

          {/* Flask neck */}
          <rect
            x="70"
            y="95"
            width="20"
            height="30"
            fill={`url(#glass-${id})`}
            stroke="#7A9BB8"
            strokeWidth="1.5"
          />

          {/* Side arm for vacuum */}
          <path
            d="M115 140 L130 130 L140 130"
            fill="none"
            stroke="#7A9BB8"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <path
            d="M115 140 L130 130 L140 130"
            fill="none"
            stroke={`url(#glass-${id})`}
            strokeWidth="4"
            strokeLinecap="round"
          />

          {/* Vacuum tube connector */}
          <rect
            x="138"
            y="125"
            width="15"
            height="10"
            fill={`url(#rubber-${id})`}
            rx="2"
          />

          {/* Vacuum hose */}
          <path
            d="M153 130 Q160 130 160 140 L160 260"
            fill="none"
            stroke="#4A4A4A"
            strokeWidth="6"
            strokeLinecap="round"
          />

          {/* Vacuum indicator */}
          {vacuumOn && (
            <g>
              {/* Air flow animation */}
              <circle cx="145" cy="130" r="2" fill="#00A86B">
                <animate
                  attributeName="cx"
                  values="145;125;115"
                  dur="0.5s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="1;0.5;0"
                  dur="0.5s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle cx="145" cy="130" r="2" fill="#00A86B">
                <animate
                  attributeName="cx"
                  values="145;125;115"
                  dur="0.5s"
                  repeatCount="indefinite"
                  begin="0.25s"
                />
                <animate
                  attributeName="opacity"
                  values="1;0.5;0"
                  dur="0.5s"
                  repeatCount="indefinite"
                  begin="0.25s"
                />
              </circle>
            </g>
          )}

          {/* Glass shine on flask */}
          <path
            d="M52 125 Q50 170 55 210"
            fill="none"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </g>

        {/* Rubber Stopper (connects funnel to flask) */}
        <ellipse
          cx="80"
          cy="93"
          rx="18"
          ry="5"
          fill={`url(#rubber-${id})`}
        />
        <rect
          x="62"
          y="85"
          width="36"
          height="10"
          fill={`url(#rubber-${id})`}
        />
        <ellipse
          cx="80"
          cy="85"
          rx="18"
          ry="5"
          fill="#4A4A4A"
        />

        {/* Buchner Funnel */}
        <g>
          {/* Funnel body (conical) */}
          <path
            d="M40 55 L65 85 L95 85 L120 55 Z"
            fill={`url(#glass-${id})`}
            stroke="#7A9BB8"
            strokeWidth="2"
          />

          {/* Funnel stem */}
          <rect
            x="72"
            y="85"
            width="16"
            height="12"
            fill={`url(#glass-${id})`}
            stroke="#7A9BB8"
            strokeWidth="1.5"
          />

          {/* Perforated plate */}
          <ellipse
            cx="80"
            cy="58"
            rx="32"
            ry="8"
            fill="rgba(200,220,240,0.5)"
            stroke="#7A9BB8"
            strokeWidth="1"
          />

          {/* Perforation holes */}
          {[
            [80, 58], [70, 58], [90, 58], [75, 55], [85, 55],
            [75, 61], [85, 61], [80, 55], [80, 61]
          ].map(([cx, cy], i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r="1.5"
              fill="#5A7A95"
            />
          ))}

          {/* Filter paper (white circle on perforated plate) */}
          <ellipse
            cx="80"
            cy="52"
            rx="28"
            ry="8"
            fill="white"
            stroke="#CCCCCC"
            strokeWidth="0.5"
          />

          {/* Precipitate on filter paper */}
          {precipitateAmount > 0 && (
            <g clipPath={`url(#filter-clip-${id})`}>
              <ellipse
                cx="80"
                cy="52"
                rx={Math.min(precipitateAmount / 100 * 25, 25)}
                ry={Math.min(precipitateAmount / 100 * 6, 6)}
                fill={`url(#precipitate-${id})`}
              />
              {/* Precipitate texture */}
              {precipitateAmount > 30 && (
                <>
                  <circle cx="75" cy="51" r="2" fill={precipitateColor} opacity="0.8" />
                  <circle cx="85" cy="52" r="2.5" fill={precipitateColor} opacity="0.8" />
                  <circle cx="80" cy="50" r="1.5" fill={precipitateColor} opacity="0.7" />
                </>
              )}
            </g>
          )}

          {/* Funnel rim */}
          <ellipse
            cx="80"
            cy="35"
            rx="40"
            ry="10"
            fill="none"
            stroke="#7A9BB8"
            strokeWidth="2"
          />
          <ellipse
            cx="80"
            cy="35"
            rx="40"
            ry="10"
            fill="rgba(200,220,240,0.3)"
          />

          {/* Liquid dripping through filter */}
          {filterProgress > 0 && filterProgress < 100 && vacuumOn && (
            <g>
              <ellipse
                cx="80"
                cy="92"
                rx="3"
                ry="4"
                fill={filtrateColor}
                opacity="0.7"
              >
                <animate
                  attributeName="cy"
                  values="92;105;92"
                  dur="0.4s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.7;0;0.7"
                  dur="0.4s"
                  repeatCount="indefinite"
                />
              </ellipse>
            </g>
          )}

          {/* Glass shine */}
          <line
            x1="48"
            y1="55"
            x2="68"
            y2="80"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </g>

        {/* Vacuum pump indicator */}
        <g>
          <rect
            x="145"
            y="240"
            width="15"
            height="20"
            fill="#3A3A3A"
            rx="3"
          />
          {/* Power LED */}
          <circle
            cx="152"
            cy="248"
            r="3"
            fill={vacuumOn ? '#00A86B' : '#666666'}
          >
            {vacuumOn && (
              <animate
                attributeName="opacity"
                values="1;0.6;1"
                dur="1s"
                repeatCount="indefinite"
              />
            )}
          </circle>
        </g>
      </g>

      {/* Progress indicator */}
      {filterProgress > 0 && filterProgress < 100 && (
        <g>
          <rect
            x="10"
            y="240"
            width="60"
            height="8"
            fill="#2A2A2A"
            rx="4"
          />
          <rect
            x="10"
            y="240"
            width={filterProgress / 100 * 60}
            height="8"
            fill="#00A86B"
            rx="4"
          />
          <text
            x="40"
            y="258"
            fontSize="8"
            fill="#888"
            textAnchor="middle"
          >
            {Math.round(filterProgress)}%
          </text>
        </g>
      )}

      {/* Labels */}
      <text
        x="80"
        y="275"
        fontSize="10"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Vacuum Filtration
      </text>
    </svg>
  )
}

export default FilterApparatus
